"use server";

import { revalidateTag, updateTag } from "next/cache";
import { gql } from "./client";

/** `moduleLike` returns Boolean! with no selection set (D34). */
type ModuleLikeData = { moduleLike: boolean };
import { verifySession } from "./dal";
import { GqlRequestError, friendlyMessage } from "./errors";
import { getRequestDict, requestLang } from "@/lib/i18n-server";
import { loadEnrolmentIds } from "@/components/learn/loadEnrolment";
import { M_ACCOUNT_SAVE_MODULE, M_ENROLL_MODULE, M_MODULE_LIKE } from "./operations";
import type { ActionResult as Result } from "@/lib/actions/result";

/** Backend codes a caller may route on. Anything else rides along as a string. */
export type ModuleActionCode = "MODULE_NOT_ENROLLABLE" | "MODULE_REQUIRES_PAYMENT" | (string & {});
export type ActionResult = Result<object, ModuleActionCode>;

/**
 * Failure result for a caught mutation error. `error` is always the localizable
 * friendly copy — the raw backend code is never user-visible — while `code`
 * carries the backend code alongside it so a caller can route on it (e.g.
 * MODULE_REQUIRES_PAYMENT → send the user to the cart). Transport/parse errors
 * aren't GqlRequestErrors and carry no code.
 */
async function failure(err: unknown): Promise<ActionResult> {
  // Request-locale error copy (D19) — getRequestDict reads the proxy's
  // x-locale header, so no action needs a lang parameter for this.
  const t = (await getRequestDict()).errors;
  if (err instanceof GqlRequestError) {
    return { ok: false, code: err.firstMessage(), error: friendlyMessage(err, t) };
  }
  return { ok: false, error: friendlyMessage(err, t) };
}

export async function likeModule(moduleId: number, like: boolean): Promise<ActionResult> {
  await verifySession();
  try {
    await gql<ModuleLikeData>(M_MODULE_LIKE, { moduleId, like }, { cache: "no-store" });
    updateTag(`module:${moduleId}`);
    return { ok: true };
  } catch (err) {
    return failure(err);
  }
}

export async function saveModule(moduleId: number, save: boolean): Promise<ActionResult> {
  await verifySession();
  try {
    await gql(M_ACCOUNT_SAVE_MODULE, { moduleId, save }, { cache: "no-store" });
    // /saved reads savedModules through no-store today, but tag anyway so
    // this doesn't break silently if that surface ever gains caching.
    updateTag("modules");
    return { ok: true };
  } catch (err) {
    return failure(err);
  }
}

export async function enroll(moduleId: number): Promise<ActionResult> {
  await verifySession();
  try {
    await gql(M_ENROLL_MODULE, { moduleId }, { cache: "no-store" });
    updateTag(`module:${moduleId}`);
    return { ok: true };
  } catch (err) {
    if (err instanceof GqlRequestError) {
      // ALREADY_ENROLLED is the one code that inverts the result: the user ends
      // up where they wanted to be, so it's a success.
      // No `code` on success: a result is narrowed on `ok`, and nothing routed
      // on this one anyway.
      if (err.hasCode("ALREADY_ENROLLED")) return { ok: true };
      // The two codes the CTA components branch on stay on hasCode() — a full
      // scan of errors[] — rather than riding failure()'s firstMessage(), which
      // only reads errors[0]. Multi-error payloads are a real GraphQL shape, so
      // matching on position would be a silent narrowing.
      if (err.hasCode("MODULE_NOT_ENROLLABLE")) {
        return {
          ok: false,
          code: "MODULE_NOT_ENROLLABLE",
          error: friendlyMessage(err, (await getRequestDict()).errors),
        };
      }
    }
    // Every other backend code still rides along on `code` (see failure()) so
    // callers COULD route on it — but none do today: MODULE_REQUIRES_PAYMENT
    // surfaces as inline error text, not a cart redirect (the paid-gate UI
    // hides the enroll CTA, so it's near-unreachable; wire routing if that
    // changes).
    return failure(err);
  }
}

// Cross-request invalidation for a module's cached fetch (loadModuleById
// uses a 300s revalidate). Called by the post-payment polling banner so
// the next router.refresh() sees Module.canView flip to true the instant
// the Stripe webhook has updated the backend. Safe to spam — Next dedupes
// by tag.
export async function refreshModuleAccess(moduleId: number): Promise<void> {
  await verifySession();
  revalidateTag(`module:${moduleId}`, "max");
}

/**
 * After a purchase, once the webhook has granted the enrolments: expire the
 * cached read of every module this member is enrolled in, at once
 * (`updateTag`). Module reads are cached for 300s, so without this the buyer
 * opening what they just bought could still meet its price and "Add to cart"
 * — a price must never show on an item that opens without paying. Called by
 * the purchase banner on "Moji tečaji" (PaidEnrolmentBanner); a cart can
 * unlock several modules, so it expires them all rather than guess which.
 */
export async function refreshPurchasedAccess(): Promise<void> {
  await verifySession();
  const ids = await loadEnrolmentIds(await requestLang());
  for (const id of ids) updateTag(`module:${id}`);
}
